import type { Point } from '../core/types';

/**
 * A path anchor. `in` and `out` are the control-point offsets relative to the
 * anchor itself, which is what keeps a knot's shape intact when it is dragged.
 * A corner knot simply has zero-length handles.
 */
export interface Knot {
  point: Point;
  in: Point;
  out: Point;
  /** Smooth knots keep their handles mirrored while one of them is dragged. */
  smooth: boolean;
}

/** How a subpath combines with everything before it, as in Photoshop's path ops. */
export type PathOp = 'add' | 'subtract' | 'intersect' | 'exclude';

export interface Subpath {
  knots: Knot[];
  closed: boolean;
  op: PathOp;
}

/** A named path, the unit the Paths panel lists and a vector mask stores. */
export interface VectorPath {
  id: string;
  name: string;
  subpaths: Subpath[];
}

/** Parametric shapes stay editable: the knots are derived, never hand-edited. */
export type ShapeGeometry =
  | { kind: 'freeform'; subpaths: Subpath[] }
  | { kind: 'rectangle'; x: number; y: number; width: number; height: number; radius: number }
  | { kind: 'ellipse'; x: number; y: number; width: number; height: number }
  | { kind: 'line'; from: Point; to: Point }
  | {
      kind: 'polygon';
      cx: number;
      cy: number;
      radius: number;
      sides: number;
      /** 1 for a polygon; below 1 it becomes a star with that inner radius ratio. */
      innerRatio: number;
      rotation: number;
    };

export type StrokeAlign = 'inside' | 'center' | 'outside';
export type StrokeJoin = 'miter' | 'round' | 'bevel';
export type StrokeCap = 'butt' | 'round' | 'square';

export interface ShapeStroke {
  width: number;
  align: StrokeAlign;
  join: StrokeJoin;
  cap: StrokeCap;
  /** Dash pattern in pixels; empty means a solid line. */
  dash: number[];
}

export function knot(point: Point, smooth = false): Knot {
  return { point, in: { x: 0, y: 0 }, out: { x: 0, y: 0 }, smooth };
}

export function emptyPath(id: string, name: string): VectorPath {
  return { id, name, subpaths: [] };
}
